import { useState } from "react";
import type { Lead, Stage } from "../../types.ts";
import { NumberField, SelectField, TextareaField, TextField } from "./LeadFields.tsx";
import { RouteMap } from "./RouteMap.tsx";
import { priorityOptions } from "./types.ts";

/** Monta o endereço de destino (rua, cidade, CEP) a partir das notas da OS.
 * O RouteMap geocoda isso (preferindo o CEP quando presente). */
function buildDestination(notes: string): string {
  const grab = (label: string) => {
    const m = notes.match(new RegExp(label + ":\\s*(.+)"));
    return m ? m[1].trim() : "";
  };
  const street = grab("Endereço do serviço");
  const city = grab("Cidade/UF").replace("/", ", ");
  const cepMatch = notes.match(/CEP:\s*(\d{5}-?\d{3})/);
  const cep = cepMatch ? cepMatch[1] : "";
  return [street, city, cep].filter(Boolean).join(", ");
}

/** Quebra as notas da OS em pares rótulo/valor para o bloco "Detalhes da OS". */
function parseOsDetails(notes: string): { label: string; value: string }[] {
  return notes
    .split("\n")
    .map((line) => {
      const idx = line.indexOf(": ");
      if (idx === -1) return null;
      return { label: line.slice(0, idx).trim(), value: line.slice(idx + 2).trim() };
    })
    .filter((x): x is { label: string; value: string } => x !== null && x.value !== "");
}

const icUser = (<svg className="fic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.5" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>);
const icPhone = (<svg className="fic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15.5 21a2 2 0 0 0 2-2v-2.2a1 1 0 0 0-.8-1l-2.3-.5a1 1 0 0 0-1 .3l-.8.9a13 13 0 0 1-5.3-5.3l.9-.8a1 1 0 0 0 .3-1l-.5-2.3a1 1 0 0 0-1-.8H4.9a2 2 0 0 0-2 2A16 16 0 0 0 15.5 21z" /></svg>);
const icMail = (<svg className="fic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3.5 7l8.5 6 8.5-6" /></svg>);
const icBook = (<svg className="fic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4z" /><path d="M5 17h13" /></svg>);
const icCash = (<svg className="fic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="6" width="19" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /></svg>);

export function LeadDetail({
  lead,
  stages,
  onClose,
  onOpenChat,
  onScheduleCadence,
  onDelete,
  onCommit,
}: {
  lead: Lead;
  stages: Stage[];
  onClose: () => void;
  onOpenChat: () => void;
  onScheduleCadence: () => void;
  onDelete: () => void;
  onCommit: (changes: Partial<Lead>) => void;
}) {
  const stageOptions = stages.map((stage) => ({ value: stage.id, label: stage.title }));
  const routeDest = buildDestination(lead.notes || "");
  const stageLabel = stageOptions.find((o) => o.value === lead.stage)?.label || "Lead";
  const isOs = (lead.notes || "").includes("Endereço do serviço:");
  const osDetails = isOs ? parseOsDetails(lead.notes || "") : [];
  const [showRoute, setShowRoute] = useState(false);

  return (
    <>
    <div className="lead-detail-overlay" onClick={onClose}>
      <aside className="lead-detail" onClick={(event) => event.stopPropagation()}>
      <header className="modal-head">
        <span className="modal-head-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="3.5" width="14" height="17" rx="2" /><path d="M9 3.5V2.5h6v1" /><path d="M9 11h6M9 15h4" /></svg>
        </span>
        <div className="modal-head-text">
          <div className="modal-head-title">{lead.name || "Sem nome"}</div>
          <div className="modal-head-sub">{stageLabel}</div>
        </div>
        <button className="modal-close" type="button" aria-label="Fechar" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </header>
      <div className="detail-body">
      <TextField lead={lead} field="name" label="Nome" icon={icUser} onCommit={onCommit} />
      <TextField lead={lead} field="phone" label="Telefone" icon={icPhone} onCommit={onCommit} />
      <TextField lead={lead} field="email" label="E-mail" icon={icMail} onCommit={onCommit} />
      <TextField lead={lead} field="course" label="Curso" icon={icBook} onCommit={onCommit} />
      <NumberField lead={lead} label="Valor" icon={icCash} onCommit={onCommit} />
      <SelectField lead={lead} field="stage" label="Estágio" options={stageOptions} onCommit={onCommit} />
      <SelectField lead={lead} field="priority" label="Prioridade" options={priorityOptions} onCommit={onCommit} />
      {isOs ? (
        <div className="detail-field full">
          <span className="detail-label">Detalhes da OS</span>
          <div className="os-details">
            {osDetails.map((d) => (
              <div className="os-row" key={d.label}>
                <span className="os-k">{d.label}</span>
                <span className="os-v">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <TextareaField lead={lead} field="notes" label="Notas" onCommit={onCommit} />
      )}
      </div>
      <div className="detail-actions">
        {routeDest ? (
          <button className="btn btn-primary" type="button" onClick={() => setShowRoute(true)}>
            <svg className="btn-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
            Ver rota
          </button>
        ) : null}
        {lead.phone ? (
          <button className="btn btn-secondary" type="button" onClick={onOpenChat}>Conversa</button>
        ) : null}
        <button className="btn btn-secondary" type="button" onClick={onScheduleCadence}>Cadência</button>
        <button className="btn btn-danger detail-del" type="button" onClick={onDelete}>Excluir</button>
      </div>
      </aside>
    </div>
    {showRoute ? <RouteMap leadId={String(lead.id)} defaultDestination={routeDest} onClose={() => setShowRoute(false)} /> : null}
    </>
  );
}
